import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { trackConversion } from '@/lib/gtag';

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  phone: z.string().trim().max(20, "Phone number must be less than 20 characters").optional(),
  message: z.string().trim().min(1, "Message is required").max(1000, "Message must be less than 1000 characters")
});

const Contact = () => {
  const { t } = useLanguage();
  const { contactInfo, hours } = useSiteSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Validate form data
      const validatedData = contactSchema.parse(formData);

      // Save to Supabase
      const { error } = await supabase
        .from('contacts')
        .insert([{
          name: validatedData.name,
          email: validatedData.email,
          phone_number: validatedData.phone || null,
          message: validatedData.message
        }]);

      if (error) throw error;

      // Track enhanced conversion
      trackConversion('contact_form_submitted', {
        email: validatedData.email,
        phone_number: validatedData.phone || undefined,
        address: {
          first_name: validatedData.name.split(' ')[0],
          last_name: validatedData.name.split(' ').slice(1).join(' ') || undefined,
        }
      });

      toast.success('Message sent! We\'ll get back to you soon.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value,
    });
  };

  return (
    <main>
      {/* Hero */}
      <section className="bg-hero-gradient text-white py-12 md:py-20 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold font-['Playfair_Display'] mb-3 md:mb-4">
            {t('contactTitle')}
          </h1>
          <p className="text-base md:text-xl text-white/90">
            {t('contactSubtitle')}
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-8 md:py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {/* Contact Form */}
            <Card className="p-4 md:p-8">
              <h2 className="text-lg md:text-2xl font-bold mb-4 md:mb-6">{t('send')} a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                <div>
                  <Label htmlFor="name" className="text-xs md:text-sm">{t('name')} *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-2 text-sm md:text-base h-9 md:h-10"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-xs md:text-sm">{t('email')} *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 text-sm md:text-base h-9 md:h-10"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-xs md:text-sm">{t('phone')}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-2 text-sm md:text-base h-9 md:h-10"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-xs md:text-sm">{t('message')} *</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="mt-2 text-sm md:text-base"
                  />
                </div>

                <Button type="submit" className="w-full text-sm md:text-base" size="lg" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : t('send')}
                </Button>
              </form>
            </Card>

            {/* Contact Info */}
            <div className="space-y-4 md:space-y-6">
              <Card className="p-4 md:p-6">
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 md:w-6 md:h-6 text-primary mr-3 md:mr-4 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-sm md:text-lg mb-2">{t('address')}</h3>
                    <p className="text-xs md:text-base text-muted-foreground">
                      {contactInfo.address}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6">
                <div className="flex items-start">
                  <Phone className="w-5 h-5 md:w-6 md:h-6 text-primary mr-3 md:mr-4 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-sm md:text-lg mb-2">{t('phone')}</h3>
                  <a
                    href={`tel:${contactInfo.phone.replace(/[^0-9+]/g, '')}`}
                    className="text-xs md:text-base text-muted-foreground hover:text-primary transition-colors"
                  >
                    {contactInfo.phone}
                  </a>
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6">
                <div className="flex items-start">
                  <Mail className="w-5 h-5 md:w-6 md:h-6 text-primary mr-3 md:mr-4 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-sm md:text-lg mb-2">{t('email')}</h3>
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="text-xs md:text-base text-muted-foreground hover:text-primary transition-colors break-all"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6">
                <div className="flex items-start">
                  <Clock className="w-5 h-5 md:w-6 md:h-6 text-primary mr-3 md:mr-4 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-sm md:text-lg mb-3">{t('hours')}</h3>
                    <div className="space-y-2 text-muted-foreground text-xs md:text-sm">
                      <div className="flex justify-between">
                        <span>{t('mondayThursday')}</span>
                        <span className="ml-4">{hours.monday_thursday}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('fridaySaturday')}</span>
                        <span className="ml-4">{hours.friday_saturday}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('sunday')}</span>
                        <span className="ml-4">{hours.sunday}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
